import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import QuantityControl from '../helper/QuantityControl'
import UnitSelector from './common/UnitSelector'
import LoadingSpinner from './common/LoadingSpinner'
import { useCart } from '../contexts/CartContext'
import orderService from '../services/orderService'
import productService from '../services/productService'
import { normalizeQuantityForBackend } from '../utils/unitHelpers'

const CartSection = () => {
    const { cart, loading, error, fetchCart, updateCartItem, removeCartItem, clearCart, getCartTotalPrice } = useCart();
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const imageApiUrl = (process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5196').replace(/\/api\/?$/,'');
    // İşlem gören ürünleri takip etmek için loading state'leri
    const [loadingItems, setLoadingItems] = useState({});
    const [isClearingCart, setIsClearingCart] = useState(false);
    const [shippingCost, setShippingCost] = useState(0);
    
    // Kargo fiyatını hesapla
    const calculateShippingCost = useCallback(async () => {
        try {
            if (!cart || (!cart.items && !cart.customPackages)) return;
            
            // Sepet ürünlerinin toplam ağırlığını hesapla
            let totalWeight = 0;
            
            // Normal ürünler - weight bilgisi yoksa API'den çek
            if (cart.items) {
                for (const item of cart.items) {
                    let itemWeight = item.weight || 0;
                    
                    // SaleType fallback: undefined ise weightInGrams'a göre belirle
                    const saleType = item.saleType !== undefined 
                        ? item.saleType 
                        : (item.weightInGrams ? 1 : 0);
                    
                    if (saleType === 1) {
                        // Kilogram bazlı ürünler için ağırlık hesapla
                        // Kilogram bazlıda quantity her zaman 1'dir, ağırlık weightInGrams'da saklanır
                        const weightInGrams = item.weightInGrams || item.weightGrams || 1000;
                        itemWeight = weightInGrams / 1000; // gram'ı kg'a çevir
                        
                        totalWeight += itemWeight; // quantity ile çarpmıyoruz çünkü zaten 1
                    } else {
                        // Adet bazlı ürünler için her adet 1kg olarak hesapla
                        totalWeight += 1 * item.quantity;
                    }
                }
            }
            
            // Custom paketler
            if (cart.customPackages) {
                totalWeight += cart.customPackages.reduce((total, pkg) => {
                    return total + pkg.items.reduce((pkgTotal, item) => {
                        return pkgTotal + (item.weightGrams || 0) * item.quantity / 1000; // gram'ı kg'a çevir
                    }, 0);
                }, 0);
            }
            
            if (totalWeight > 0) {
                const response = await orderService.calculateShippingCost(totalWeight);
                setShippingCost(response.cost || 0);
            } else {
                setShippingCost(0);
            }
        } catch (error) {
            console.error('Kargo fiyatı hesaplanırken hata:', error);
            setShippingCost(0);
        }
    }, [cart]);

    useEffect(() => {
        fetchCart();
    }, []);

    useEffect(() => {
        calculateShippingCost();
    }, [cart, calculateShippingCost]);

    const handleQuantityChange = async (itemId, newQuantity, newUnit, weightGrams) => {
        // İlgili ürün için loading durumu başlat
        setLoadingItems(prev => ({ ...prev, [itemId]: true }));
        
        // WeightGrams parametresini ekle
        const result = await updateCartItem(itemId, newQuantity, undefined, weightGrams);
        
        // İşlem tamamlandı, loading durumunu kaldır
        setLoadingItems(prev => ({ ...prev, [itemId]: false }));
        
        if (!result.success) {
            setMessage(result.message || 'Miktar güncellenirken bir hata oluştu.');
            setIsError(true);
        }
    };

    const handleRemoveItem = async (itemId) => {
        // İlgili ürün için loading durumu başlat
        setLoadingItems(prev => ({ ...prev, [itemId]: true }));
        
        const result = await removeCartItem(itemId);
        
        // Başarılı olsa bile hemen kaldırmayalım, geçiş animasyonu için bekletelim 
        setTimeout(() => {
            setLoadingItems(prev => ({ ...prev, [itemId]: false }));
        }, 500);
        
        if (!result.success) {
            setMessage(result.message || 'Ürün sepetten çıkarılırken bir hata oluştu.');
            setIsError(true);
        }
    };

    const handleClearCart = async () => {
        setIsClearingCart(true);
        
        const result = await clearCart();
        
        setTimeout(() => {
            setIsClearingCart(false);
        }, 500);
        
        if (!result.success) {
            setMessage(result.message || 'Sepet temizlenirken bir hata oluştu.');
            setIsError(true);
        }
    };

    // Ürün görseli için URL oluşturma
    const getProductImageUrl = (imageUrl) => {
        if (!imageUrl) return "assets/images/logo/logo.png";
        return imageUrl.startsWith('http') ? imageUrl : `${imageApiUrl}${imageUrl}`;
    };

    // İlk yükleme sırasında loading göstergesi
    if (loading && !cart) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <LoadingSpinner 
                        size="lg" 
                        color="primary" 
                        text="Sepetiniz yükleniyor..."
                    />
                </div>
            </section>
        );
    }

    if (error || isError) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="alert alert-danger" role="alert">
                        {message || error || 'Sepet yüklenirken bir hata oluştu.'}
                    </div>
                    <div className="text-center mt-3">
                        <button 
                            className="btn btn-primary"
                            onClick={() => {
                                setIsError(false);
                                setMessage('');
                                fetchCart();
                            }}
                        >
                            Tekrar Dene
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    if (!cart || (( !cart.items || cart.items.length === 0 ) && ( !cart.customPackages || cart.customPackages.length === 0 ))) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="text-center py-10">
                        <div className="empty-cart-animation mb-30">
                            <i className="ph ph-shopping-cart-simple text-6xl text-main-600"></i>
                        </div>
                        <h3 className="h3 mb-12 text-gray-800">Sepetiniz Boş</h3>
                        <p className="mb-32 text-gray-600 mx-auto" style={{ maxWidth: '500px' }}>
                            Sepetinize henüz ürün eklemediniz. Taze ürünlerimizi keşfetmek ve sepetinizi doldurmak için alışverişe başlayabilirsiniz.
                        </p>
                        <Link 
                            to="/shop" 
                            className="btn bg-main-600 text-white hover-bg-main-800 py-14 px-32 mb-32 rounded-pill fw-medium"
                        >
                            <i className="ph ph-shopping-bag me-2"></i>
                            Alışverişe Başla
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="cart py-80">
            <div className="container container-lg">
                <div className="row gy-4">
                    <div className="col-xl-9 col-lg-8">
                        <div className={`cart-table border border-gray-100 rounded-8 px-40 py-48 ${isClearingCart ? 'cart-table-fading' : ''}`}>
                            {message && (
                                <div className={`alert ${isError ? 'alert-danger' : 'alert-success'} mb-4`} role="alert">
                                    {message}
                                </div>
                            )}
                            <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                                <table className="table style-three">
                                    <thead>
                                        <tr>
                                            <th className="h6 mb-0 text-lg fw-bold">Sil</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Ürün Adı</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Fiyat</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Miktar</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Toplam</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.customPackages && cart.customPackages.length > 0 && cart.customPackages.map(pkg => {
                                            const pkgTotalPrice = pkg.items.reduce((sum, item) => sum + (item.totalPrice || (item.unitPrice * item.quantity)), 0);
                                            const isPkgLoading = loadingItems[pkg.id] || false;
                                            return (
                                                <tr key={pkg.id} className={`cart-item custom-package ${isPkgLoading ? 'cart-item-loading' : ''}`}>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                                                            onClick={() => handleRemoveItem(pkg.id)}
                                                            disabled={isPkgLoading}
                                                        >
                                                            {isPkgLoading ? (
                                                                <LoadingSpinner size="sm" color="danger" />
                                                            ) : (
                                                                <i className="ph ph-x-circle text-2xl d-flex" />
                                                            )}
                                                            Kaldır
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div className="table-product d-flex align-items-center gap-24">
                                                            <div className="table-product__thumb border border-gray-100 rounded-8 flex-center">
                                                                <img
                                                                    src="assets/images/logo/logo.png"
                                                                    alt="Özelleştirilmiş Paket"
                                                                    style={{ maxWidth: "80px", maxHeight: "80px", objectFit: "contain" }}
                                                                />
                                                            </div>
                                                            <div className="table-product__content text-start">
                                                                <h6 className="title text-lg fw-semibold mb-8">Özelleştirilmiş Paket</h6>
                                                                <p className="mb-2 text-sm">{pkg.items.map(item => `${item.productName} (${item.weightGrams * item.quantity}g)`).join(", ")}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="text-lg h6 mb-0 fw-semibold">{pkgTotalPrice.toFixed(2)} ₺</span>
                                                    </td>
                                                    <td>
                                                        <span className="text-lg h6 mb-0 fw-semibold">1</span>
                                                    </td>
                                                    <td>
                                                        <span className="text-lg h6 mb-0 fw-semibold">{pkgTotalPrice.toFixed(2)} ₺</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {cart.items.map((item, index) => {
                                            const isLoading = loadingItems[item.id];
                                            const price = typeof item.price === 'number' ? item.price : 0;
                                            const discountedPrice = typeof item.discountedPrice === 'number' ? item.discountedPrice : null;
                                            const displayPrice = discountedPrice !== null ? discountedPrice : price;
                                            
                                            // Miktar ve birim bilgisi için hesaplama
                                            const saleType = item.saleType !== undefined ? item.saleType : (item.weightGrams || item.weightInGrams ? 1 : 0);
                                            
                                            // Toplam fiyat hesaplama - kilogram bazlı ürünler için doğru hesaplama
                                            let itemTotalPrice = 0;
                                            if (saleType === 1) {
                                                // Kilogram bazlı ürün - weightInGrams'ı kg'a çevir ve fiyatla çarp
                                                const weightInGrams = item.weightInGrams || item.weightGrams || 1000;
                                                const weightInKg = weightInGrams / 1000;
                                                itemTotalPrice = displayPrice * weightInKg;
                                            } else {
                                                // Adet bazlı ürün - quantity ile çarp
                                                itemTotalPrice = displayPrice * (item.quantity || 0);
                                            }

                                            return (
                                                <tr key={item.id || index} 
                                                    className={`cart-item ${isLoading ? 'cart-item-loading' : ''}`}
                                                >
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                                                            onClick={() => handleRemoveItem(item.id)}
                                                            disabled={isLoading}
                                                        >
                                                            {isLoading ? (
                                                                <LoadingSpinner size="sm" color="danger" />
                                                            ) : (
                                                                <i className="ph ph-x-circle text-2xl d-flex" />
                                                            )}
                                                            Kaldır
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div className="table-product d-flex align-items-center gap-24">
                                                            <Link
                                                                to={`/product-details/${item.productId}`}
                                                                className="table-product__thumb border border-gray-100 rounded-8 flex-center"
                                                            >
                                                                <img
                                                                    src={getProductImageUrl(item.productImageUrl)}
                                                                    alt={item.productName}
                                                                    style={{ maxWidth: "80px", maxHeight: "80px", objectFit: "contain" }}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = "assets/images/logo/logo.png";
                                                                    }}
                                                                />
                                                            </Link>
                                                            <div className="table-product__content text-start">
                                                                <h6 className="title text-lg fw-semibold mb-8">
                                                                    <Link
                                                                        to={`/product-details/${item.productId}`}
                                                                        className="link text-line-2"
                                                                    >
                                                                        {item.productName}
                                                                    </Link>
                                                                </h6>
                                                                {item.note && (
                                                                    <p className="mb-2 text-sm">{item.note}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {discountedPrice !== null ? (
                                                            <div>
                                                                <span className="text-lg h6 mb-0 fw-semibold">{displayPrice.toFixed(2)} ₺</span>
                                                                <span className="text-decoration-line-through text-sm d-block text-gray-400">{price.toFixed(2)} ₺</span>
                                                                <span className="text-xs text-gray-500 d-block">/ {item.saleType === 1 ? 'Kg' : 'Adet'}</span>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <span className="text-lg h6 mb-0 fw-semibold">{price.toFixed(2)} ₺</span>
                                                                <span className="text-xs text-gray-500 d-block">/ {item.saleType === 1 ? 'Kg' : 'Adet'}</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="quantity-control-wrapper position-relative">
                                                            {(item.saleType !== undefined ? item.saleType : (item.weightGrams || item.weightInGrams ? 1 : 0)) === 1 ? (
                                                                // Kilogram bazlı ürün - UnitSelector kullan
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <UnitSelector
                                                                        saleType={1}
                                                                        minimumOrderWeight={item.minimumOrderWeight || 250}
                                                                        price={item.price}
                                                                        discountedPrice={item.discountedPrice}
                                                                        onQuantityChange={(quantity, unit) => {
                                                                            // Birim bilgisini kullanarak gram'a çevir
                                                                            const weightGrams = normalizeQuantityForBackend(quantity, unit, 1);
                                                                            handleQuantityChange(item.id, 1, unit, weightGrams);
                                                                        }}
                                                                        initialQuantity={item.weightGrams || item.weightInGrams}
                                                                        initialUnit="gram"
                                                                        showPrice={false}
                                                                        size="sm"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                // Adet bazlı ürün - QuantityControl kullan
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <QuantityControl 
                                                                        initialQuantity={item.quantity} 
                                                                        onQuantityChange={(newQuantity) => handleQuantityChange(item.id, newQuantity)}
                                                                    />
                                                                </div>
                                                            )}
                                                            {isLoading && (
                                                                <div className="quantity-overlay">
                                                                    <LoadingSpinner size="sm" color="primary" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="text-lg h6 mb-0 fw-semibold">{itemTotalPrice.toFixed(2)} ₺</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="d-flex flex-wrap justify-content-between gap-16 mt-48">
                                <Link
                                    to="/shop"
                                    className="btn border border-gray-200 text-heading text-dark hover-bg-gray-200 py-12 px-24 rounded-8 flex-center gap-16 fw-medium"
                                >
                                    <i className="ph ph-arrow-left" />
                                    Alışverişe Devam Et
                                </Link>
                                <button
                                    className="btn border border-gray-200 text-heading text-dark hover-bg-gray-200 py-12 px-24 rounded-8 flex-center gap-16 fw-medium"
                                    onClick={handleClearCart}
                                    disabled={isClearingCart}
                                >
                                    {isClearingCart ? (
                                        <>
                                            <LoadingSpinner size="sm" color="white" />
                                            <span style={{ marginLeft: '8px' }}>Temizleniyor...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="ph ph-trash" />
                                            Sepeti Temizle
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-4">
                        <div className="cart-checkout-box border border-gray-100 rounded-8 px-24 py-32">
                            <h5 className="text-xl text-gray-900 fw-semibold mb-24">Sipariş Özeti</h5>
                            <div className="cart-subtotal border-y border-gray-100 py-16">
                                <ul>
                                    <li className="d-flex justify-content-between align-items-center flex-wrap mb-12">
                                        <span className="text-gray-700 fw-medium text-md">Ara Toplam</span>
                                        <span className="text-primary-600 fw-semibold text-lg">{(typeof getCartTotalPrice() === 'number' ? getCartTotalPrice() : 0).toFixed(2)} ₺</span>
                                    </li>
                                    <li className="d-flex justify-content-between align-items-center flex-wrap mb-12">
                                        <span className="text-gray-700 fw-medium text-md">Kargo</span>
                                        <span className="text-heading fw-semibold text-md">
                                            {shippingCost > 0 ? `${shippingCost.toFixed(2)} ₺` : 'Ücretsiz'}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                            <div className="cart-total d-flex justify-content-between align-items-center flex-wrap py-16 mb-24 border-bottom border-gray-100">
                                <span className="text-gray-700 fw-medium text-md">Toplam</span>
                                <span className="text-primary-600 fw-semibold text-lg">
                                    {((typeof getCartTotalPrice() === 'number' ? getCartTotalPrice() : 0) + shippingCost).toFixed(2)} ₺
                                </span>
                            </div>
                            <Link
                                to="/checkout"
                                className="cart-checkout-btn btn btn-lg bg-primary-600 text-white hover-bg-primary-800 py-20 px-24 rounded-8 flex-center gap-16 fw-semibold w-100"
                            >
                                <i className="ph ph-shopping-cart-simple text-lg" />
                                Siparişi Tamamla
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <style jsx>{`
                .cart-item-loading {
                    opacity: 0.7;
                    pointer-events: none;
                    position: relative;
                }
                
                .quantity-loading {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                
                .cart-table-fading {
                    opacity: 0.7;
                    pointer-events: none;
                    transition: opacity 0.3s ease;
                }
                
                .cart-item {
                    transition: opacity 0.3s ease;
                }
                
                .quantity-control-wrapper {
                    position: relative;
                }
                
                .quantity-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(255, 255, 255, 0.7);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-radius: 4px;
                    z-index: 5;
                }
            `}</style>
        </section>
    );
};

export default CartSection;